import {
  RecordingProcessStatus,
  transformRawDrives,
  ListDriveInfo,
  RawDriveInfo,
} from '@ouster/webviz-react-components';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UserOrg } from '../../types';
import { BASE_ORG_API } from '../../utils/constants';
import { fetchWithAuth } from '../../utils/requests';

const INDEX_CACHE_TTL_MS = 2 * 60 * 60 * 1000;
const MAX_ORG_CACHE_ENTRIES = 10;

/** Compact shape returned by GET /search-index */
export interface SearchIndexRecord {
  id: number;
  recording_title: string | null;
  source_prefix: string | null;
  upload_status: string | null;
  processed_at: string | null;
  start_time: string | null;
  stop_time: string | null;
  osf_generation_status: string | null;
  visualization_status: string | null;
  slam_generation_status: string | null;
  data_type: string | null;
  data_category: string | null;
  uploaded_by: string | null;
  uploaded_by_id: number | null;
  organization_id: number;
  publication_id: number | null;
  publication_uuid: string | null;
  tags: string[];
  sdk_sensor_channel_type: string | null;
  sensor_info: Array<{
    sdk_prod_sn: string | null;
    sdk_prod_line: string | null;
    sdk_prod_type: string | null;
    sdk_prod_generation: string | null;
    sdk_prod_hw_rev: string | null;
    sdk_prod_beam_spacing: string | null;
    sdk_prod_resolution: string | null;
    sdk_fw_version_core: string | null;
    sdk_lidar_mode: string | null;
    sdk_fw_branch: string | null;
    sdk_fw_build_rev: string | null;
    sdk_fw_image_rev: string | null;
    sdk_sensor_channel_type: string | null;
  }>;
}

type WorkerResponse =
  | { type: 'ready'; count: number }
  | { type: 'results'; ids: number[]; query: string };

interface OrgIndexCacheEntry {
  records: SearchIndexRecord[];
  transformed: Map<number, ListDriveInfo>;
  transformedOrdered: ListDriveInfo[];
  sizeBytes: number;
  updatedAt: number;
  lastAccessAt: number;
}

const orgIndexCache = new Map<number, OrgIndexCacheEntry>();

const nowMs = () => Date.now();

const isStale = (entry: OrgIndexCacheEntry) =>
  nowMs() - entry.lastAccessAt > INDEX_CACHE_TTL_MS;

const estimateCacheSizeBytes = (records: SearchIndexRecord[]): number =>
  // Heuristic only: avoid expensive JSON serialization on large datasets.
  records.length * 256;

const setOrgCacheEntry = (
  orgId: number,
  records: SearchIndexRecord[],
  transformed: Map<number, ListDriveInfo>,
  transformedOrdered: ListDriveInfo[]
) => {
  const timestamp = nowMs();
  orgIndexCache.set(orgId, {
    records,
    transformed,
    transformedOrdered,
    sizeBytes: estimateCacheSizeBytes(records),
    updatedAt: timestamp,
    lastAccessAt: timestamp,
  });
};

const touchOrgCacheEntry = (orgId: number) => {
  const entry = orgIndexCache.get(orgId);
  if (!entry) return;
  entry.lastAccessAt = nowMs();
};

const purgeStaleOrgCacheEntries = (protectedOrgId?: number) => {
  const toDelete: number[] = [];
  orgIndexCache.forEach((entry, orgId) => {
    if (protectedOrgId === orgId) return;
    if (isStale(entry)) {
      toDelete.push(orgId);
    }
  });
  toDelete.forEach((orgId) => {
    orgIndexCache.delete(orgId);
  });
};

const enforceOrgCacheLimit = (protectedOrgId?: number) => {
  // Primary strategy: remove smallest entries first.
  while (orgIndexCache.size > MAX_ORG_CACHE_ENTRIES) {
    const candidates = Array.from(orgIndexCache.entries())
      .filter(([orgId]) => orgId !== protectedOrgId)
      .sort((a, b) => a[1].sizeBytes - b[1].sizeBytes);

    if (candidates.length === 0) break;
    orgIndexCache.delete(candidates[0][0]);
  }

  // Fallback: if still above limit, remove stale by oldest access first.
  if (orgIndexCache.size > MAX_ORG_CACHE_ENTRIES) {
    const staleCandidates = Array.from(orgIndexCache.entries())
      .filter(([orgId, entry]) => orgId !== protectedOrgId && isStale(entry))
      .sort((a, b) => a[1].lastAccessAt - b[1].lastAccessAt);

    staleCandidates.forEach(([orgId]) => {
      if (orgIndexCache.size > MAX_ORG_CACHE_ENTRIES) {
        orgIndexCache.delete(orgId);
      }
    });
  }
};

/** Transform a compact search-index record into a ListDriveInfo for the table. */
const toListDriveInfo = (rec: SearchIndexRecord): ListDriveInfo => {
  // Build a RawDriveInfo-compatible object with defaults for missing fields
  const raw = {
    id: rec.id,
    data_type: rec.data_type ?? 'SDK',
    organization_id: rec.organization_id,
    upload_status: rec.upload_status ?? '',
    source_bucket: '',
    source_prefix: rec.source_prefix ?? '',
    processed_at: rec.processed_at ?? '',
    data_category: rec.data_category ?? null,
    start_time: rec.start_time ?? null,
    stop_time: rec.stop_time ?? null,
    osf_bucket: null,
    osf_prefix: null,
    osf_generation_date: null,
    osf_generation_status: rec.osf_generation_status ?? '',
    slam_bucket: null,
    slam_prefix: null,
    slam_generation_date: null,
    slam_generation_status: rec.slam_generation_status ?? '',
    uploaded_by: rec.uploaded_by ?? null,
    uploaded_by_id: rec.uploaded_by_id ?? null,
    publication_uuid: rec.publication_uuid ?? null,
    sdk_sensor_channel_type: rec.sdk_sensor_channel_type ?? null,
    public_title: null,
    sensor_info: rec.sensor_info.map((s) => ({
      sdk_fw_version_core: s.sdk_fw_version_core ?? null,
      sdk_lidar_mode: s.sdk_lidar_mode ?? null,
      sdk_prod_beam_spacing: s.sdk_prod_beam_spacing ?? null,
      sdk_prod_generation: s.sdk_prod_generation ?? null,
      sdk_prod_hw_rev: s.sdk_prod_hw_rev ?? null,
      sdk_prod_line: s.sdk_prod_line ?? null,
      sdk_prod_pn: null,
      sdk_prod_resolution: s.sdk_prod_resolution ?? null,
      sdk_prod_sn: s.sdk_prod_sn ?? null,
      sdk_prod_type: s.sdk_prod_type ?? null,
      sdk_fw_branch: s.sdk_fw_branch ?? null,
      sdk_fw_build_rev: s.sdk_fw_build_rev ?? null,
      sdk_fw_image_rev: s.sdk_fw_image_rev ?? null,
      sdk_sensor_channel_type: s.sdk_sensor_channel_type ?? null,
    })),
    tags: rec.tags,
    visualization_status: rec.visualization_status ?? 'LEGACY',
    recording_title: rec.recording_title ?? undefined,
  };

  const drive = transformRawDrives(raw as unknown as RawDriveInfo);

  // Apply the same visualization_status logic as the private query config
  if (drive.visualization_status === 'LEGACY') return drive;
  if (drive.status === RecordingProcessStatus.Failed) return drive;
  if (drive.visualization_status === 'UNKNOWN') {
    return { ...drive, status: RecordingProcessStatus.Processing };
  }
  if (
    drive.visualization_status === 'NOT_VIEWABLE' &&
    (drive.status === RecordingProcessStatus.Ready ||
      drive.status === RecordingProcessStatus.SlamError)
  ) {
    return { ...drive, status: RecordingProcessStatus.VizAlert };
  }
  return drive;
};

interface UseSearchIndexResult {
  /** Whether the search index is still loading / building. */
  isIndexLoading: boolean;
  /** Whether the search index finished building successfully. */
  isIndexReady: boolean;
  /** Organization id the current index was built for, or null if none. */
  indexOrgId: number | null;
  /** Number of records in the index. */
  indexSize: number;
  /** Whether current org has a fresh in-memory cache entry. */
  hasFreshOrgCache: boolean;
  /** Whether refreshIndex() initiated a rebuild that is still in flight. */
  isIndexRefreshInFlight: boolean;
  /** All indexed drives transformed to ListDriveInfo in source order. */
  allRecords: ListDriveInfo[];
  /** Matching drives as ListDriveInfo[], or null if no active search/filter. */
  searchResults: ListDriveInfo[] | null;
  /** Refetch search-index and rebuild local worker index. */
  refreshIndex: () => Promise<void>;
  /** Patch one recording title in local index and cache without full refetch. */
  updateIndexedRecordingTitle: (
    driveId: number,
    recordingTitle: string | null | undefined
  ) => void;
  /** Patch one transformed drive in local cache without touching worker index. */
  updateIndexedDrive: (
    driveId: number,
    updater: (drive: ListDriveInfo) => ListDriveInfo
  ) => void;
  /** Remove one recording from local index/cache without full refetch. */
  removeIndexedRecording: (driveId: number) => void;
  /** Optimistically insert a new recording into local index and cache. */
  addIndexedRecording: (record: SearchIndexRecord) => void;
}

// Sensor-level filter keys that should match against any sensor in sensor_info
const SENSOR_FILTER_KEYS = new Set([
  'sdk_prod_type',
  'sdk_prod_sn',
  'sdk_prod_line',
  'sdk_prod_generation',
  'sdk_prod_hw_rev',
  'sdk_prod_beam_spacing',
  'sdk_prod_resolution',
  'sdk_fw_version_core',
  'sdk_lidar_mode',
  'sdk_fw_branch',
  'sdk_fw_build_rev',
  'sdk_fw_image_rev',
]);

/** Check if a record matches all active filters (AND across filter groups). */
const matchesFilters = (
  rec: SearchIndexRecord,
  filters: Record<string, string[]>
): boolean => {
  const entries = Object.entries(filters);
  return entries.every(([key, values]) => {
    if (values.length === 0) return true;

    // Tags: record must have at least one matching tag
    if (key === 'tags') {
      return values.some((v) => rec.tags.includes(v));
    }

    // Data category: compare raw category name against filter values
    if (key === 'data_category') {
      if (!rec.data_category) return false;
      // Filter values are full category names; rec.data_category is also raw
      return values.includes(rec.data_category);
    }

    // Uploaded by
    if (key === 'uploaded_by') {
      return rec.uploaded_by ? values.includes(rec.uploaded_by) : false;
    }

    // Sensor-level fields: any sensor must match
    if (SENSOR_FILTER_KEYS.has(key)) {
      return rec.sensor_info.some((s) => {
        const val = s[key as keyof typeof s];
        if (val === null || val === undefined) return false;
        return values.includes(String(val));
      });
    }

    return true;
  });
};

/**
 * Hook that fetches the compact search index for an org, ships it to a
 * Web Worker for MiniSearch indexing, and returns fully-transformed
 * ListDriveInfo results for any active search term and/or filters.
 */
export function useSearchIndex(
  curUserOrg: UserOrg | undefined,
  searchTerm: string,
  filters: Record<string, string[]> = {}
): UseSearchIndexResult {
  const workerRef = useRef<Worker | null>(null);
  const [isIndexLoading, setIsIndexLoading] = useState(true);
  const [isIndexReady, setIsIndexReady] = useState(false);
  const [indexOrgId, setIndexOrgId] = useState<number | null>(null);
  const [indexSize, setIndexSize] = useState(0);
  const [matchingIds, setMatchingIds] = useState<Set<number> | null>(null);
  const [isIndexRefreshInFlight, setIsIndexRefreshInFlight] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const refreshResolverRef = useRef<(() => void) | null>(null);
  const currentOrgIdRef = useRef<number | null>(null);
  const [indexDataVersion, setIndexDataVersion] = useState(0);

  const hasFreshOrgCache =
    !!curUserOrg?.id &&
    (() => {
      const entry = orgIndexCache.get(curUserOrg.id);
      return !!entry && !isStale(entry);
    })();

  // Store all search index records for display after search
  const recordsRef = useRef<SearchIndexRecord[]>([]);
  const recordsByIdRef = useRef<Map<number, SearchIndexRecord>>(new Map());
  const recordPositionByIdRef = useRef<Map<number, number>>(new Map());
  // Pre-computed ListDriveInfo for all records (built once when index loads)
  const transformedRef = useRef<Map<number, ListDriveInfo>>(new Map());
  const transformedOrderedRef = useRef<ListDriveInfo[]>([]);

  const refreshIndex = useCallback(
    (): Promise<void> =>
      new Promise((resolve) => {
        if (currentOrgIdRef.current !== null) {
          orgIndexCache.delete(currentOrgIdRef.current);
        }
        setIsIndexRefreshInFlight(true);
        refreshResolverRef.current = resolve;
        setReloadVersion((prev) => prev + 1);
      }),
    []
  );

  const updateIndexedRecordingTitle = useCallback(
    (driveId: number, recordingTitle: string | null | undefined) => {
      const existing = recordsByIdRef.current.get(driveId);
      if (!existing) return;
      const recordPosition = recordPositionByIdRef.current.get(driveId);
      if (recordPosition === undefined) return;

      const nextTitle = recordingTitle?.trim() ? recordingTitle : null;
      const updatedRecord: SearchIndexRecord = {
        ...existing,
        recording_title: nextTitle,
      };

      recordsByIdRef.current.set(driveId, updatedRecord);
      const nextRecords = [...recordsRef.current];
      nextRecords[recordPosition] = updatedRecord;
      recordsRef.current = nextRecords;

      const updatedDrive = toListDriveInfo(updatedRecord);
      transformedRef.current.set(driveId, updatedDrive);
      const nextTransformedOrdered = [...transformedOrderedRef.current];
      nextTransformedOrdered[recordPosition] = updatedDrive;
      transformedOrderedRef.current = nextTransformedOrdered;

      const currentOrgId = currentOrgIdRef.current;
      if (currentOrgId !== null) {
        const cached = orgIndexCache.get(currentOrgId);
        if (cached) {
          cached.records = recordsRef.current;
          cached.transformed = transformedRef.current;
          cached.transformedOrdered = transformedOrderedRef.current;
          cached.sizeBytes = estimateCacheSizeBytes(cached.records);
          cached.updatedAt = nowMs();
          cached.lastAccessAt = cached.updatedAt;
        }
      }

      setIndexDataVersion((prev) => prev + 1);

      if (workerRef.current) {
        workerRef.current.postMessage({
          type: 'updateRecord',
          record: updatedRecord,
        });
        const trimmed = searchTerm.trim();
        if (trimmed.length >= 2) {
          workerRef.current.postMessage({ type: 'search', query: trimmed });
        } else {
          setMatchingIds(null);
        }
      }
    },
    [searchTerm]
  );

  const updateIndexedDrive = useCallback(
    (driveId: number, updater: (drive: ListDriveInfo) => ListDriveInfo) => {
      const recordPosition = recordPositionByIdRef.current.get(driveId);
      if (recordPosition === undefined) return;

      const currentDrive = transformedRef.current.get(driveId);
      if (!currentDrive) return;

      const updatedDrive = updater(currentDrive);
      transformedRef.current.set(driveId, updatedDrive);

      const nextTransformedOrdered = [...transformedOrderedRef.current];
      nextTransformedOrdered[recordPosition] = updatedDrive;
      transformedOrderedRef.current = nextTransformedOrdered;

      const currentOrgId = currentOrgIdRef.current;
      if (currentOrgId !== null) {
        const cached = orgIndexCache.get(currentOrgId);
        if (cached) {
          cached.transformed = transformedRef.current;
          cached.transformedOrdered = transformedOrderedRef.current;
          cached.updatedAt = nowMs();
          cached.lastAccessAt = cached.updatedAt;
        }
      }

      setIndexDataVersion((prev) => prev + 1);
    },
    []
  );

  const removeIndexedRecording = useCallback(
    (driveId: number) => {
      const recordPosition = recordPositionByIdRef.current.get(driveId);
      if (recordPosition === undefined) return;

      const nextRecords = [...recordsRef.current];
      nextRecords.splice(recordPosition, 1);
      recordsRef.current = nextRecords;

      recordsByIdRef.current.delete(driveId);

      const nextPositions = new Map(recordPositionByIdRef.current);
      nextPositions.delete(driveId);
      nextPositions.forEach((idx, id) => {
        if (idx > recordPosition) {
          nextPositions.set(id, idx - 1);
        }
      });
      recordPositionByIdRef.current = nextPositions;

      transformedRef.current.delete(driveId);
      const nextTransformedOrdered = [...transformedOrderedRef.current];
      nextTransformedOrdered.splice(recordPosition, 1);
      transformedOrderedRef.current = nextTransformedOrdered;

      const currentOrgId = currentOrgIdRef.current;
      if (currentOrgId !== null) {
        const cached = orgIndexCache.get(currentOrgId);
        if (cached) {
          cached.records = recordsRef.current;
          cached.transformed = transformedRef.current;
          cached.transformedOrdered = transformedOrderedRef.current;
          cached.sizeBytes = estimateCacheSizeBytes(cached.records);
          cached.updatedAt = nowMs();
          cached.lastAccessAt = cached.updatedAt;
        }
      }

      setIndexSize(recordsRef.current.length);
      setIndexDataVersion((prev) => prev + 1);

      if (workerRef.current) {
        workerRef.current.postMessage({ type: 'removeRecord', id: driveId });
        const trimmed = searchTerm.trim();
        if (trimmed.length >= 2) {
          workerRef.current.postMessage({ type: 'search', query: trimmed });
        } else {
          setMatchingIds(null);
        }
      }
    },
    [searchTerm]
  );

  const addIndexedRecording = useCallback(
    (record: SearchIndexRecord) => {
      // Skip if record already exists
      if (recordsByIdRef.current.has(record.id)) return;

      // Prepend to records
      const nextRecords = [record, ...recordsRef.current];
      recordsRef.current = nextRecords;
      recordsByIdRef.current.set(record.id, record);

      // Shift all existing positions by +1 and add the new one at 0
      const nextPositions = new Map<number, number>();
      nextPositions.set(record.id, 0);
      recordPositionByIdRef.current.forEach((idx, id) => {
        nextPositions.set(id, idx + 1);
      });
      recordPositionByIdRef.current = nextPositions;

      // Build transformed entry and prepend
      const drive = toListDriveInfo(record);
      transformedRef.current.set(record.id, drive);
      transformedOrderedRef.current = [drive, ...transformedOrderedRef.current];

      // Update org cache
      const currentOrgId = currentOrgIdRef.current;
      if (currentOrgId !== null) {
        const cached = orgIndexCache.get(currentOrgId);
        if (cached) {
          cached.records = recordsRef.current;
          cached.transformed = transformedRef.current;
          cached.transformedOrdered = transformedOrderedRef.current;
          cached.sizeBytes = estimateCacheSizeBytes(cached.records);
          cached.updatedAt = nowMs();
          cached.lastAccessAt = cached.updatedAt;
        }
      }

      setIndexSize(recordsRef.current.length);
      setIndexDataVersion((prev) => prev + 1);

      // Add to worker search index
      if (workerRef.current) {
        workerRef.current.postMessage({
          type: 'updateRecord',
          record,
        });
        const trimmed = searchTerm.trim();
        if (trimmed.length >= 2) {
          workerRef.current.postMessage({ type: 'search', query: trimmed });
        } else {
          setMatchingIds(null);
        }
      }
    },
    [searchTerm]
  );

  // ------------------------------------------------------------------
  // Boot worker + fetch data
  // ------------------------------------------------------------------
  useEffect(() => {
    const resetState = () => {
      setIsIndexLoading(false);
      setIsIndexReady(false);
      setIndexOrgId(null);
      setIndexSize(0);
      setMatchingIds(null);
      setIsIndexRefreshInFlight(false);
      recordsRef.current = [];
      recordsByIdRef.current = new Map();
      recordPositionByIdRef.current = new Map();
      transformedRef.current = new Map();
      transformedOrderedRef.current = [];
      setIndexDataVersion((prev) => prev + 1);
    };

    if (!curUserOrg?.id) {
      currentOrgIdRef.current = null;
      resetState();
      refreshResolverRef.current?.();
      refreshResolverRef.current = null;
      return undefined;
    }

    currentOrgIdRef.current = curUserOrg.id;

    setIsIndexLoading(true);
    setIsIndexReady(false);
    setIndexOrgId(null);
    setIndexSize(0);
    setMatchingIds(null);
    recordsRef.current = [];
    recordsByIdRef.current = new Map();
    recordPositionByIdRef.current = new Map();
    transformedRef.current = new Map();
    transformedOrderedRef.current = [];
    setIndexDataVersion((prev) => prev + 1);

    const orgId = curUserOrg.id;

    purgeStaleOrgCacheEntries(orgId);

    const cached = orgIndexCache.get(orgId);
    if (cached && !isStale(cached)) {
      touchOrgCacheEntry(orgId);
      recordsRef.current = cached.records;
      recordsByIdRef.current = new Map(
        cached.records.map((rec) => [rec.id, rec])
      );
      recordPositionByIdRef.current = new Map(
        cached.records.map((rec, idx) => [rec.id, idx])
      );
      transformedRef.current = new Map(cached.transformed);
      transformedOrderedRef.current = [...cached.transformedOrdered];
      setIndexDataVersion((prev) => prev + 1);

      const worker = new Worker(new URL('../../searchWorker', import.meta.url));
      workerRef.current = worker;

      worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
        const msg = e.data;

        if (msg.type === 'ready') {
          setIsIndexReady(true);
          setIndexOrgId(orgId);
          setIsIndexLoading(false);
          setIndexSize(cached.records.length);
          setIsIndexRefreshInFlight(false);
          refreshResolverRef.current?.();
          refreshResolverRef.current = null;
        }

        if (msg.type === 'results') {
          setMatchingIds(new Set(msg.ids));
        }
      };

      worker.postMessage({ type: 'init', data: cached.records });

      return () => {
        worker.terminate();
        workerRef.current = null;
      };
    }

    const worker = new Worker(new URL('../../searchWorker', import.meta.url));
    workerRef.current = worker;

    worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
      const msg = e.data;

      if (msg.type === 'ready') {
        // Pre-transform all records now so search results are instant
        const map = new Map<number, ListDriveInfo>();
        const ordered = recordsRef.current.map((rec) => {
          const transformed = toListDriveInfo(rec);
          map.set(rec.id, transformed);
          return transformed;
        });
        transformedRef.current = map;
        transformedOrderedRef.current = ordered;
        setOrgCacheEntry(orgId, recordsRef.current, map, ordered);
        enforceOrgCacheLimit(orgId);
        setIndexDataVersion((prev) => prev + 1);
        setIsIndexReady(true);
        setIndexOrgId(orgId);
        setIsIndexLoading(false);
        setIndexSize(msg.count);
        setIsIndexRefreshInFlight(false);
        refreshResolverRef.current?.();
        refreshResolverRef.current = null;
      }

      if (msg.type === 'results') {
        setMatchingIds(new Set(msg.ids));
      }
    };

    // Fetch the compact search index
    const fetchIndex = async () => {
      try {
        const url = `${BASE_ORG_API}/${curUserOrg.id}/recordings/search-index`;
        const resp = await fetchWithAuth(url);

        if (!resp.ok) {
          console.error(
            'Failed to fetch search index:',
            resp.status,
            resp.statusText
          );
          setIsIndexReady(false);
          setIndexOrgId(null);
          setIsIndexLoading(false);
          setIsIndexRefreshInFlight(false);
          refreshResolverRef.current?.();
          refreshResolverRef.current = null;
          return;
        }

        const json = (await resp.json()) as { data: SearchIndexRecord[] };
        recordsRef.current = json.data;
        recordsByIdRef.current = new Map(json.data.map((rec) => [rec.id, rec]));
        recordPositionByIdRef.current = new Map(
          json.data.map((rec, idx) => [rec.id, idx])
        );
        worker.postMessage({ type: 'init', data: json.data });
      } catch (err) {
        console.error('Error fetching search index:', err);
        setIsIndexReady(false);
        setIndexOrgId(null);
        setIsIndexLoading(false);
        setIsIndexRefreshInFlight(false);
        refreshResolverRef.current?.();
        refreshResolverRef.current = null;
      }
    };

    fetchIndex();

    return () => {
      worker.terminate();
      workerRef.current = null;
    };
  }, [curUserOrg?.id, reloadVersion]);

  // ------------------------------------------------------------------
  // Trigger search in worker when searchTerm changes
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!workerRef.current || isIndexLoading) return undefined;

    const trimmed = searchTerm.trim();
    if (trimmed.length < 2) {
      setMatchingIds(null);
      return undefined;
    }

    workerRef.current.postMessage({ type: 'search', query: trimmed });
    return undefined;
  }, [searchTerm, isIndexLoading]);

  // ------------------------------------------------------------------
  // Build search results from matching IDs + filters
  // ------------------------------------------------------------------
  const hasActiveFilters = Object.values(filters).some((v) => v.length > 0);

  const allRecords = useMemo(() => {
    const version = indexDataVersion;
    return version >= 0 ? transformedOrderedRef.current : [];
  }, [indexDataVersion]);

  const searchResults = useMemo((): ListDriveInfo[] | null => {
    const version = indexDataVersion;
    if (version < 0) return null;

    const hasSearch = matchingIds !== null;

    // Nothing active — let server handle it
    if (!hasSearch && !hasActiveFilters) return null;

    // Search-only / search+filters: iterate matching IDs in relevance order.
    if (hasSearch) {
      const results: ListDriveInfo[] = [];
      matchingIds.forEach((id) => {
        const rec = recordsByIdRef.current.get(id);
        if (!rec) return;
        if (hasActiveFilters && !matchesFilters(rec, filters)) return;
        const drive = transformedRef.current.get(id);
        if (drive) results.push(drive);
      });
      return results;
    }

    // Filter-only: preserve source order.
    return recordsRef.current
      .filter((rec) => matchesFilters(rec, filters))
      .map((rec) => transformedRef.current.get(rec.id))
      .filter((drive): drive is ListDriveInfo => drive !== undefined);
  }, [matchingIds, filters, hasActiveFilters, indexDataVersion]);

  return {
    isIndexLoading,
    isIndexReady,
    indexOrgId,
    indexSize,
    hasFreshOrgCache,
    isIndexRefreshInFlight,
    allRecords,
    searchResults,
    refreshIndex,
    updateIndexedRecordingTitle,
    updateIndexedDrive,
    removeIndexedRecording,
    addIndexedRecording,
  };
}
